import { useState } from "react";
import { useAuthContext } from "@/hooks/useAuthContext";
import {
  useListCasesQuery,
  useCreateCaseMutation,
  useGetCaseQuery,
  useAssignUserToCaseMutation,
  useRemoveUserFromCaseMutation,
  useListAccessRequestsQuery,
  useApproveAccessRequestMutation,
  useDenyAccessRequestMutation,
  type CaseAssignment,
} from "@/redux/api/cases.rtkq";
import { useGetOfficeMembersQuery } from "@/redux/api/offices.rtkq";

const PERMISSION_LABELS: Record<string, string> = {
  viewer: "צפייה",
  contributor: "תורם",
  editor: "עורך",
};

const CasesPage = () => {
  const { getAccountId, isAccountAdmin } = useAuthContext();
  const officeId = getAccountId() || "";
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [page, setPage] = useState(1);
  const [showCreate, setShowCreate] = useState(false);
  const [newTitle, setNewTitle] = useState("");
  const [newDescription, setNewDescription] = useState("");
  const [expandedCaseId, setExpandedCaseId] = useState<string | null>(null);

  const { data, isLoading } = useListCasesQuery(
    { officeId, search: search || undefined, status: statusFilter || undefined, page, limit: 20 },
    { skip: !officeId }
  );
  const [createCase] = useCreateCaseMutation();

  const handleCreate = async () => {
    if (!newTitle.trim()) return;
    try {
      await createCase({ officeId, title: newTitle.trim(), description: newDescription }).unwrap();
      setNewTitle("");
      setNewDescription("");
      setShowCreate(false);
    } catch (err: any) {
      alert(err.data?.message || "Failed to create case");
    }
  };

  return (
    <div className="container mx-auto p-6 max-w-5xl" dir="rtl">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold text-foreground">תיקים</h1>
        {isAccountAdmin() && (
          <button
            onClick={() => setShowCreate(!showCreate)}
            className="px-4 py-2 bg-chen-900 text-white rounded-lg hover:opacity-90"
          >
            + תיק חדש
          </button>
        )}
      </div>

      {isAccountAdmin() && (
        <AccessRequestsPanel officeId={officeId} />
      )}

      {showCreate && (
        <div className="bg-card border border-border rounded-lg p-4 mb-6">
          <h2 className="text-lg font-semibold mb-3">יצירת תיק חדש</h2>
          <div className="space-y-3">
            <input
              type="text"
              placeholder="שם התיק"
              value={newTitle}
              onChange={(e) => setNewTitle(e.target.value)}
              className="w-full px-3 py-2 border border-border rounded-lg bg-background text-foreground"
            />
            <textarea
              placeholder="תיאור (לא חובה)"
              value={newDescription}
              onChange={(e) => setNewDescription(e.target.value)}
              className="w-full px-3 py-2 border border-border rounded-lg bg-background text-foreground"
              rows={2}
            />
            <div className="flex gap-2">
              <button onClick={handleCreate} className="px-4 py-2 bg-chen-900 text-white rounded-lg hover:opacity-90">
                צור
              </button>
              <button onClick={() => setShowCreate(false)} className="px-4 py-2 border border-border rounded-lg text-foreground">
                ביטול
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="flex gap-2 mb-4">
        <input
          type="text"
          placeholder="חיפוש תיקים..."
          value={search}
          onChange={(e) => { setSearch(e.target.value); setPage(1); }}
          className="flex-1 px-3 py-2 border border-border rounded-lg bg-background text-foreground"
        />
        <select
          value={statusFilter}
          onChange={(e) => { setStatusFilter(e.target.value); setPage(1); }}
          className="px-3 py-2 border border-border rounded-lg bg-background text-foreground"
        >
          <option value="">כל הסטטוסים</option>
          <option value="active">פעיל</option>
          <option value="archived">בארכיון</option>
          <option value="closed">סגור</option>
        </select>
      </div>

      {isLoading ? (
        <div className="text-muted-foreground">טוען תיקים...</div>
      ) : data?.cases.length === 0 ? (
        <div className="text-center py-12 text-muted-foreground">
          <p className="text-lg">לא נמצאו תיקים</p>
        </div>
      ) : (
        <>
          <div className="space-y-2">
            {data?.cases.map((c) => (
              <div key={c.caseId}>
                <div
                  onClick={() => setExpandedCaseId(expandedCaseId === c.caseId ? null : c.caseId)}
                  className={`bg-card border rounded-lg p-4 transition-colors cursor-pointer ${
                    expandedCaseId === c.caseId ? "border-chen-900" : "border-border hover:border-chen-900/50"
                  }`}
                >
                  <div className="flex justify-between items-start">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <h3 className="font-medium text-foreground truncate">{c.title}</h3>
                        {c.caseNumber && (
                          <span className="text-xs font-semibold text-indigo-600 bg-indigo-50 px-1.5 py-0.5 rounded shrink-0">
                            #{c.caseNumber}
                          </span>
                        )}
                      </div>
                      <div className="text-sm text-muted-foreground mt-1">
                        {(c as any).client?.name && (
                          <span className="me-3">{(c as any).client.name}</span>
                        )}
                        {c.updatedAt && (
                          <span>{new Date(c.updatedAt).toLocaleDateString("he-IL")}</span>
                        )}
                      </div>
                    </div>
                    <span className={`text-xs px-2 py-1 rounded-full shrink-0 ms-2 ${
                      c.status?.toLowerCase() === "active"
                        ? "bg-green-50 text-green-700"
                        : "bg-background border border-border text-muted-foreground"
                    }`}>
                      {c.status?.toLowerCase() === "active" ? "פעיל"
                        : c.status?.toLowerCase() === "archived" ? "בארכיון"
                        : c.status?.toLowerCase() === "closed" ? "סגור"
                        : c.status}
                    </span>
                  </div>
                </div>

                {expandedCaseId === c.caseId && (
                  <CaseDetailPanel officeId={officeId} caseId={c.caseId} isAdmin={isAccountAdmin()} />
                )}
              </div>
            ))}
          </div>

          {data && data.totalPages > 1 && (
            <div className="flex justify-center gap-2 mt-4">
              <button
                disabled={page <= 1}
                onClick={() => setPage(page - 1)}
                className="px-3 py-1 border border-border rounded text-foreground disabled:opacity-50"
              >
                הקודם
              </button>
              <span className="px-3 py-1 text-muted-foreground">
                עמוד {page} מתוך {data.totalPages}
              </span>
              <button
                disabled={page >= data.totalPages}
                onClick={() => setPage(page + 1)}
                className="px-3 py-1 border border-border rounded text-foreground disabled:opacity-50"
              >
                הבא
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
};

const CaseDetailPanel = ({
  officeId,
  caseId,
  isAdmin,
}: {
  officeId: string;
  caseId: string;
  isAdmin: boolean;
}) => {
  const { data: caseDetail, isLoading } = useGetCaseQuery(
    { officeId, caseId },
    { skip: !officeId }
  );
  const { data: membersData } = useGetOfficeMembersQuery(officeId, {
    skip: !officeId || !isAdmin,
  });
  const [assignUser] = useAssignUserToCaseMutation();
  const [removeUser] = useRemoveUserFromCaseMutation();
  const [addPermission, setAddPermission] = useState("viewer");

  if (isLoading) {
    return (
      <div className="border border-t-0 border-border rounded-b-lg p-4 bg-muted/30 text-sm text-muted-foreground">
        טוען פרטי תיק...
      </div>
    );
  }

  if (!caseDetail) return null;

  const assignments: CaseAssignment[] = caseDetail.assignments || [];
  const assignedIds = new Set(assignments.map((a) => a.userId));
  const unassigned = (membersData?.members || []).filter(
    (m) => !assignedIds.has(m.userId)
  );

  return (
    <div className="border border-t-0 border-border rounded-b-lg p-4 bg-muted/30 space-y-4">
      {caseDetail.description && caseDetail.description !== "initial case description" && (
        <div>
          <span className="text-xs font-semibold text-muted-foreground">תיאור</span>
          <p className="text-sm text-foreground mt-0.5">{caseDetail.description}</p>
        </div>
      )}

      <div>
        <div className="flex items-center justify-between mb-2">
          <span className="text-xs font-semibold text-muted-foreground">
            משתמשים משויכים ({assignments.length})
          </span>
        </div>

        {assignments.length === 0 ? (
          <p className="text-xs text-muted-foreground">אין משתמשים משויכים</p>
        ) : (
          <div className="space-y-1">
            {assignments.map((a) => (
              <div
                key={a.userId}
                className="flex items-center justify-between text-sm bg-card rounded-lg px-3 py-2"
              >
                <div className="min-w-0 flex-1">
                  <span className="font-medium">{a.displayName || a.email}</span>
                  {a.displayName && (
                    <span className="text-muted-foreground text-xs ms-2">{a.email}</span>
                  )}
                </div>
                <div className="flex items-center gap-2 ms-2">
                  {isAdmin ? (
                    <>
                      <select
                        value={a.permission}
                        onChange={(e) =>
                          assignUser({ officeId, caseId, userId: a.userId, permission: e.target.value })
                        }
                        className="text-xs px-2 py-1 border border-border rounded bg-background"
                      >
                        <option value="viewer">צפייה</option>
                        <option value="contributor">תורם</option>
                        <option value="editor">עורך</option>
                      </select>
                      <button
                        onClick={() => removeUser({ officeId, caseId, userId: a.userId })}
                        className="text-red-500 hover:text-red-700 text-xs"
                      >
                        הסר
                      </button>
                    </>
                  ) : (
                    <span className="text-xs text-muted-foreground">
                      {PERMISSION_LABELS[a.permission] || a.permission}
                    </span>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {isAdmin && unassigned.length > 0 && (
        <div>
          <span className="text-xs font-semibold text-muted-foreground mb-2 block">
            הוסף משתמש
          </span>
          <div className="flex gap-2 flex-wrap">
            <select
              value={addPermission}
              onChange={(e) => setAddPermission(e.target.value)}
              className="text-xs px-2 py-1 border border-border rounded bg-background"
            >
              <option value="viewer">צפייה</option>
              <option value="contributor">תורם</option>
              <option value="editor">עורך</option>
            </select>
            {unassigned.map((m) => (
              <button
                key={m.userId}
                onClick={() =>
                  assignUser({ officeId, caseId, userId: m.userId, permission: addPermission })
                }
                className="text-xs px-3 py-1 border border-border rounded-lg hover:bg-chen-900 hover:text-white transition-colors"
              >
                + {m.displayName || m.email}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

const AccessRequestsPanel = ({ officeId }: { officeId: string }) => {
  const { data: requests } = useListAccessRequestsQuery(
    { officeId },
    { skip: !officeId },
  );
  const [approve] = useApproveAccessRequestMutation();
  const [deny] = useDenyAccessRequestMutation();

  if (!requests || requests.length === 0) return null;

  return (
    <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-6">
      <h2 className="text-sm font-semibold mb-3 text-amber-800">
        בקשות גישה ממתינות ({requests.length})
      </h2>
      <div className="space-y-2">
        {requests.map((r) => (
          <div
            key={r.id}
            className="flex items-center justify-between bg-white rounded-lg p-3 border border-amber-100"
          >
            <div className="min-w-0 flex-1">
              <div className="text-sm font-medium">
                {r.user?.displayName || r.user?.email || "משתמש"}
              </div>
              <div className="text-xs text-muted-foreground">
                תיק: {r.case?.title || r.caseId}
                {r.case?.caseNumber && ` (#${r.case.caseNumber})`}
                {" — "}
                {PERMISSION_LABELS[r.requestedPermission] || r.requestedPermission}
              </div>
              {r.message && (
                <div className="text-xs text-muted-foreground mt-0.5 italic">
                  "{r.message}"
                </div>
              )}
            </div>
            <div className="flex gap-1 ms-2">
              <button
                onClick={() => approve({ officeId, requestId: r.id })}
                className="text-xs px-3 py-1 bg-green-600 text-white rounded-lg hover:bg-green-700"
              >
                אשר
              </button>
              <button
                onClick={() => deny({ officeId, requestId: r.id })}
                className="text-xs px-3 py-1 border border-border rounded-lg hover:bg-muted"
              >
                דחה
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default CasesPage;
